import { AllowFunc } from '@fullcalendar/core';
import { addDays, isSameDay, subMilliseconds } from 'date-fns';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { getScheduleByDate } from 'shared/utils/schedules';

export type ExtendedProps = {
  isDeadline?: boolean;
  weekStartsOn?: WeekDays;
  occurrenceId?: ID;
  task?: Task;
  taskId?: ID;
  goalId?: ID;
  lifeAreaId?: ID;
  name?: string;
  url?: string;
  color?: string;
  originDate?: Timestamp;
};

export const eventAllowBase: AllowFunc = (dropInfo, draggedEvent) =>
  !!draggedEvent &&
  // make sure a deadline event cannot be moved out of the all-day section
  !(draggedEvent.extendedProps.isDeadline && !dropInfo.allDay) &&
  // make sure an event cannot cover multiple days
  isSameDay(dropInfo.start, subMilliseconds(dropInfo.end, 1));

export const eventAllowHabit: AllowFunc = (dropInfo, draggedEvent) => {
  // basic checks on the dragged event
  if (!draggedEvent) {
    return false;
  }

  // if not a habit or repeating task, this check is not needed
  if (!draggedEvent.extendedProps.task?.schedules?.length) {
    return true;
  }

  // make sure we have all necessary properties
  if (!draggedEvent.extendedProps.weekStartsOn) {
    return false;
  }

  // get the active schedule for the drop date to have the correct calculations
  const schedule = getScheduleByDate(
    draggedEvent.extendedProps.task?.schedules,
    draggedEvent.start ?? dropInfo.start,
  );
  // no schedule means no plan, so we can allow the drop
  if (!schedule) {
    return true;
  }

  // get the date range of the current displayed range by fullcalendar
  // @ts-ignore - this is a private property, but we need it to get the correct dates
  const { start, end } = draggedEvent._context.calendarApi.currentData
    ?.dateProfile?.currentRange ?? {
    start: addDays(dropInfo.start, -16),
    end: addDays(dropInfo.start, 16),
  };

  const weekStartsOn = draggedEvent.extendedProps.weekStartsOn as WeekDays;

  // get the planning for the task
  const planning = draggedEvent.extendedProps.task
    ? getTaskPlanning(draggedEvent.extendedProps.task, {
        startDate: start,
        endDate: end,
        weekStartsOn,
        strictEndDate: true,
      })
    : [];

  return (
    // ensure the date where the event started is allowed
    (draggedEvent.start && isSameDay(dropInfo.start, draggedEvent.start)) ||
    (draggedEvent.extendedProps.originDate &&
      isSameDay(dropInfo.start, draggedEvent.extendedProps.originDate)) ||
    // ensure other planned dates are not allowed; only one event per date
    !planning.find(({ date }) => isSameDay(date, dropInfo.start))
  );
};

export const eventAllow: AllowFunc = (dropInfo, draggedEvent) =>
  eventAllowBase(dropInfo, draggedEvent) &&
  eventAllowHabit(dropInfo, draggedEvent);
